/**
 * 初期配置の決定的な生成。
 *
 * 初期配置はステージ定義の `seed` から決まる。同じステージ URL は常に同じ初期盤面を与える
 * （FR-019 のブックマーク再訪と、テストの再現性の双方のため）。`core` は `Math.random()` を
 * 呼ばず、PRNG をここに持つ。
 *
 * 生成はビルド時に 1 ステージ 1 回だけ行う。棄却サンプリングのコストは無視できる。
 *
 * 根拠: spec/04a-puzzle-core.md §6、02-requirements.md FR-016。
 */

import { positionsOf } from './board';
import { computeGroups } from './groups';
import { at } from './internal';
import { IDENTITY_TARGET, isSolved } from './stage';
import type { Placement, TargetLayout } from './types';

/**
 * 棄却サンプリングの試行回数の上限。
 *
 * 条件を満たす配置の割合は最も厳しい `8:8` でも約 4.3% で、平均 23 回程度で収束する
 * （`04a` §6.2）。1000 回連続で外れる確率は事実上ゼロであり、ここに達したのは
 * 確率的な不運ではなく `τ` か分割数の指定が壊れていることを意味する。
 */
export const MAX_SHUFFLE_ATTEMPTS = 1000;

/**
 * mulberry32。32 ビットの seed から `[0, 1)` の一様乱数を返す。
 *
 * 状態は 32 ビット 1 語だけで、同じ seed からは常に同じ列が出る。暗号用途ではない。
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `0..N−1` の一様ランダムな置換（Fisher–Yates）。 */
export function shuffled(N: number, rand: () => number): Placement {
  const xs = Array.from({ length: N }, (_, i) => i);
  for (let i = N - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const swapped = at(xs, i);
    xs[i] = at(xs, j);
    xs[j] = swapped;
  }
  return xs;
}

/**
 * 初期配置の条件 (a)〜(e)（`04a` §6.2）を満たすか。
 *
 * - (a)(b) 第 1 段階・第 2 段階のいずれも、既に解けていない
 * - (c) `τ` 基準で結合する隣接対が無い（初期状態で秘文字がつながっていない）
 * - (d) 不動点が `N/4` 以下
 * - (e) `id` 基準で結合する隣接対が無い（初期状態で画像もつながっていない）
 *
 * (e) は結合判定には影響しない。第 1 段階の判定基準は `τ` であり、`id` 基準の隣接は
 * グループを作らないためである。これは**見た目**のための条件で、原画像の一部が最初から
 * つながって見えると「画像を揃えるゲームで既にいくらか揃っている」と誤解され、
 * 第 1 段階の目標が秘文字であることに気づきにくくなる。
 */
export function satisfiesInitialConditions(
  placement: Placement,
  tau: TargetLayout,
  C: number,
): boolean {
  const N = placement.length;
  const positionOf = positionsOf(placement);
  const identity = IDENTITY_TARGET(N);

  if (isSolved(positionOf, tau)) return false; // (a)
  if (isSolved(positionOf, identity)) return false; // (b)
  if (computeGroups(placement, tau, C).count !== N) return false; // (c)
  if (fixedPoints(positionOf, tau) > N / 4) return false; // (d)
  if (computeGroups(placement, identity, C).count !== N) return false; // (e)
  return true;
}

/**
 * seed から条件 (a)〜(e) を満たす初期配置を生成する。
 *
 * 上限回数内に得られなければ例外を投げる。呼び出し側（ビルド時のマニフェスト検証）は
 * これを検証規則 V-6 の違反としてビルドを失敗させる。
 */
export function initialPlacement(
  seed: number,
  C: number,
  R: number,
  tau: TargetLayout,
  maxAttempts: number = MAX_SHUFFLE_ATTEMPTS,
): Placement {
  const N = C * R;
  const rand = mulberry32(seed);
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const placement = shuffled(N, rand);
    if (satisfiesInitialConditions(placement, tau, C)) return placement;
  }
  throw new Error(
    `V-6 違反: seed ${seed}（${C}:${R}）から §6.2 の条件を満たす初期配置が ` +
      `${maxAttempts} 回の試行で得られませんでした。τ か分割数の指定を確認してください。`,
  );
}

/** `positionOf[x] === target[x]` を満たすピースの数。 */
function fixedPoints(positionOf: readonly number[], target: TargetLayout): number {
  let count = 0;
  for (const [piece, cell] of target.entries()) {
    if (positionOf[piece] === cell) count++;
  }
  return count;
}
